import {useContext, useEffect, useState} from 'react';
import {SurveyModel} from 'survey-core';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {ToastType} from 'patient-ping-remedy/packages/toast';
import Button, {StyleType} from 'patient-ping-remedy/packages/button';
import {AxiosError} from 'axios';
import SurveyJSForm from '@common/survey_js/SurveyJSForm';
import {GENERICERROR} from '@manual_encounters/constants/BannerMessages';
import {CareJourneyDetailsResponse, CareJourneyStep} from '@/api/dto/care-journey';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useActiveRoster} from '@/store/roster_store';
import {useAlertStore} from '@/store/alert_store';
import {useSurveyJSForm} from '@/hooks/useSurveyJSForm';
import DisplayHelpers from '@/helpers/display_helpers';
import Helpers from '@/helpers/helpers';
import {
  ButtonWrapper,
  FormWrapper,
  SkippedMessage
} from '@/components/pages/tasks/task_edit/progress/TaskStepForm.styles';

type Props = {
  careJourneyStep: CareJourneyStep;
  careJourneyDetails: CareJourneyDetailsResponse;
  disabled: boolean;
}

export default function WorkflowStepForm(
  { careJourneyStep, careJourneyDetails, disabled }: Props
) {
  const { carecoApi } = useContext(CarecoApiContext);
  const currentRoster = useActiveRoster();
  const { addAlert } = useAlertStore();
  const { scrollToError } = useSurveyJSForm();
  const queryClient = useQueryClient();
  const [model, setModel] = useState<SurveyModel | null>(null);

  const isReadOnly = disabled
    || careJourneyStep.status === 'completed'
    || !careJourneyStep.taskId;

  useEffect(() => {
    if (!careJourneyStep.surveyJson) {
      setModel(null);
      return;
    }
    try {
      const surveyModel = new SurveyModel(careJourneyStep.surveyJson);
      surveyModel.getAllQuestions()
        .forEach(question => question.readOnly = isReadOnly);
      if (careJourneyStep.formData) {
        surveyModel.mergeData(JSON.parse(careJourneyStep.formData));
      }
      setModel(surveyModel);
    } catch {
      setModel(null);
    }
  }, [careJourneyStep.surveyJson, careJourneyStep.formData, isReadOnly]);

  const { mutate, isPending } = useMutation({
    mutationFn: async ({ formData }: { formData: string; skipped: boolean }) => {
      await carecoApi?.submitClientWorkflowStep(
        careJourneyDetails.careJourneyInstanceId,
        currentRoster!.id,
        careJourneyStep.stepName,
        careJourneyStep.taskId!,
        formData
      );
    },
    onSuccess(_data, variables) {
      addAlert({
        type: ToastType.SUCCESS,
        content: DisplayHelpers.humanize(careJourneyStep.stepName)
          + (variables.skipped ? ' form skipped.' : ' form submitted successfully.')
      });
      void queryClient.refetchQueries({
        queryKey: ['careJourneyDetails',
          careJourneyDetails.careJourneyInstanceId]
      });
    },
    onError(error: AxiosError) {
      Helpers.alertOnError(
        `${GENERICERROR} ${Helpers.traceId(error)}`, error);
    },
  });

  if (careJourneyStep.skipped && careJourneyStep.status === 'completed') {
    return <SkippedMessage>This step was skipped.</SkippedMessage>;
  }

  if (!model) {
    return null;
  }

  const handleSubmit = () => {
    if (model.validate()) {
      mutate({ formData: JSON.stringify(model.data), skipped: false });
    } else {
      scrollToError();
    }
  };

  const handleSkip = () => {
    mutate({ formData: JSON.stringify({ skipped: true }), skipped: true });
  };

  return (
    <>
      <FormWrapper>
        <SurveyJSForm surveyModel={model} />
      </FormWrapper>
      {!isReadOnly && (
        <ButtonWrapper>
          {careJourneyStep.skippable && (
            <Button
              styleType={StyleType.SECONDARY}
              disabled={isPending || !currentRoster}
              onClick={handleSkip}
            >
              Skip
            </Button>
          )}
          <Button disabled={isPending || !currentRoster} onClick={handleSubmit}>
            Save and submit
          </Button>
        </ButtonWrapper>
      )}
    </>
  );
}
